<div class="mx-auto px-4 pb-20">
    @if(accessDenied) {
    <!-- Access Denied Message -->
    <div class="flex flex-col items-center justify-center min-h-screen py-12">
        <div class="max-w-md w-full bg-white rounded-2xl shadow-lg border border-red-200 p-8">
            <div class="flex flex-col items-center text-center">
                <!-- Lock Icon -->
                <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                    <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                </div>

                <!-- Title -->
                <h2 class="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>

                <!-- Message -->
                <p class="text-gray-600 mb-6">
                    You don't have permission to view this project. Only project managers, project members, assigned
                    team members, and administrators can access this project.
                </p>

                <!-- Redirect Info -->
                <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 w-full">
                    <p class="text-sm text-blue-800">
                        <svg class="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Redirecting to projects list in 3 seconds...
                    </p>
                </div>

                <!-- Action Button -->
                <button (click)="goBack()"
                    class="bg-blue-600 text-white px-6 py-3 rounded-xl font-medium hover:bg-blue-700 transition-colors shadow-sm cursor-pointer">
                    Go to Projects Now
                </button>
            </div>
        </div>
    </div>
    } @else if(loading) {
    <div class="animate-pulse space-y-4">
        <div class="h-8 bg-gray-200 rounded w-1/3"></div>
        <div class="h-32 bg-gray-200 rounded w-full"></div>
    </div>
    }@else if(project) {
    @let budget = project.estimatedHours || 0;
    @let encoded = encodedHours;
    @let eD = getEncodedVsBudgetDiff();
    @let fusedTeam = getFusedTeam();
    @let statusInfo = getProjectStatusInfo();
    @let deadline = getProjectDeadline();

    <!-- Breadcrumb -->
    <div class="flex items-center gap-1.5 mt-5 mb-3">
        <button (click)="goBack()"
            class="inline-flex items-center gap-1 text-sm text-gray-400 hover:text-gray-700 transition-colors cursor-pointer group">
            <svg class="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
            Projects
        </button>
    </div>

    <!-- Title row -->
    <div class="flex items-start justify-between gap-4 mb-5">
        <!-- Left: status dot + title + subtitle -->
        <div class="min-w-0 flex-1">
            <div class="flex items-center gap-2.5 min-w-0">
                <span class="w-2.5 h-2.5 rounded-full shrink-0" [ngClass]="statusInfo.dot"></span>
                @if(isEditingProjectName && user.admin()) {
                <input type="text" [(ngModel)]="editedProjectName" (blur)="saveProjectName()"
                    (keydown.enter)="saveProjectName()" (keydown.escape)="cancelEditProjectName()" #projectNameInput
                    class="text-2xl font-bold text-gray-900 border-2 border-blue-500 rounded-lg px-2 py-0.5 focus:outline-none flex-1 min-w-0">
                } @else {
                <h1 class="text-2xl font-bold text-gray-900 truncate" title="{{ project.projectName }}">{{ project.projectName }}</h1>
                @if(user.admin()) {
                <button (click)="startEditProjectName()"
                    class="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md cursor-pointer shrink-0" title="Rename">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                </button>
                }
                }
            </div>

            <!-- Subtitle: client · échéance · status -->
            <div class="flex items-center gap-2 mt-1.5 text-sm text-gray-500 flex-wrap pl-5">
                @if(project.client) {
                <span class="inline-flex items-center gap-1.5 group">
                    {{ project.client.name }}
                    @if(user.admin()) {
                    <button (click)="unlinkClient(); $event.stopPropagation()"
                        class="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 cursor-pointer transition-opacity" title="Remove client">
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                    }
                </span>
                } @else if (user.admin()) {
                <div class="relative inline-flex">
                    <input type="text" [(ngModel)]="clientSearchQuery" (input)="filterClientsForSearch()" (focus)="filterClientsForSearch()" (blur)="closeClientResults()"
                        placeholder="+ Add client" class="text-sm bg-transparent outline-none text-gray-500 placeholder-gray-400 hover:text-gray-700 focus:text-gray-700 w-40 border-b border-dashed border-transparent hover:border-gray-300 focus:border-blue-400 transition-colors">
                    @if(showClientResults && filteredClientsForSearch.length > 0) {
                    <div class="absolute left-0 top-full mt-1 z-[100] bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden min-w-[200px] max-h-[200px] overflow-y-auto">
                        @for(c of filteredClientsForSearch; track c.id) {
                        <button (mousedown)="selectClientFromSearch(c)"
                            class="w-full text-left px-4 py-2.5 text-xs text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-colors cursor-pointer flex items-center gap-2 border-b border-gray-50 last:border-0">
                            <span class="w-5 h-5 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-[9px] font-bold shrink-0">{{ c.name.charAt(0).toUpperCase() }}</span>
                            {{ c.name }}
                        </button>
                        }
                    </div>
                    }
                    @if(showClientResults && filteredClientsForSearch.length === 0 && clientSearchQuery) {
                    <div class="absolute left-0 top-full mt-1 z-[100] bg-white border border-gray-200 rounded-xl shadow-lg px-4 py-3 text-xs text-gray-400 italic min-w-[200px]">No client found</div>
                    }
                </div>
                }

                @if(deadline) {
                <span class="text-gray-300">·</span>
                <span>Due {{ deadline | date:'d MMM' }}</span>
                }

                <span class="text-gray-300">·</span>
                <span class="font-semibold" [ngClass]="statusInfo.text">{{ statusInfo.label }}</span>
            </div>
        </div>

        <!-- Right: avancement + team + actions -->
        <div class="flex items-center gap-4 shrink-0">
            <!-- Avancement -->
            <div class="text-right">
                <div class="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Progress</div>
                <div class="text-2xl font-bold text-gray-900 leading-none mt-0.5">{{ getAvancement() }}%</div>
                @if(budget > 0) {
                <div class="text-[11px] font-medium mt-0.5" [ngClass]="eD >= 0 ? 'text-gray-400' : 'text-red-500'">{{ encoded | number:'1.0-1' }}h / {{ budget }}h</div>
                }
            </div>

            <!-- Team avatars -->
            <button (click)="showTeamList = !showTeamList" class="flex items-center -space-x-2 cursor-pointer" title="Team">
                @for(person of fusedTeam.slice(0, 3); track person.id) {
                <span class="inline-flex items-center justify-center w-8 h-8 rounded-full text-[10px] font-bold border-2 border-white shadow-sm"
                    [class.bg-emerald-100]="person.role === 'manager'" [class.text-emerald-700]="person.role === 'manager'"
                    [class.bg-blue-100]="person.role === 'member'" [class.text-blue-700]="person.role === 'member'"
                    [class.bg-indigo-100]="person.role === 'assignee'" [class.text-indigo-700]="person.role === 'assignee'">
                    {{ getTeamInitials(person) }}
                </span>
                }
                @if(fusedTeam.length > 3) {
                <span class="inline-flex items-center justify-center w-8 h-8 rounded-full text-[10px] font-bold bg-gray-100 text-gray-500 border-2 border-white shadow-sm">+{{ fusedTeam.length - 3 }}</span>
                }
                @if(fusedTeam.length === 0) {
                <span class="inline-flex items-center justify-center w-8 h-8 rounded-full text-gray-300 border-2 border-dashed border-gray-200">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" /></svg>
                </span>
                }
            </button>

            <!-- Actions cluster -->
            @if(canManageProject()) {
            <div class="flex items-center gap-1">
                <button (click)="toggleAddLink()" class="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer" title="Links">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                </button>
                <button (click)="showEditProjectModal = true" class="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer" title="Project settings">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                </button>
            </div>
            }
        </div>
    </div>

    <!-- Add Link Form -->
    @if(showAddLinkForm && canManageProject()) {
    <div class="mb-4">
        <div class="flex gap-2 items-end flex-wrap p-3 bg-gray-50 rounded-lg border border-gray-200">
            <select [(ngModel)]="newLinkType" (ngModelChange)="onLinkTypeChange()" class="px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg outline-none bg-white cursor-pointer">
                <option value="Drive">Drive</option><option value="Other">Other</option>
            </select>
            <input type="text" [(ngModel)]="newLinkName" [readonly]="newLinkType === 'Drive'" placeholder="Name" class="px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg outline-none flex-1 min-w-[80px]" [class.bg-gray-100]="newLinkType === 'Drive'">
            <input type="url" [(ngModel)]="newLinkUrl" (keydown.enter)="addLink()" (keydown.escape)="cancelAddLink()" placeholder="https://..." class="px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg outline-none flex-1 min-w-[120px]">
            <button (click)="addLink()" [disabled]="!newLinkUrl || !newLinkName" class="px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 disabled:opacity-50 cursor-pointer font-medium">Add</button>
            <button (click)="cancelAddLink()" class="px-2.5 py-1.5 bg-gray-200 text-gray-600 text-xs rounded-lg hover:bg-gray-300 cursor-pointer">Cancel</button>
        </div>
    </div>
    }

    <!-- Links row (if any) -->
    @if(project.links && project.links.length > 0) {
    <div class="flex items-center gap-2 mb-4 flex-wrap">
        @for(link of project.links; track $index) {
        <div class="group inline-flex items-center gap-1.5 px-2 py-0.5 bg-gray-50 border border-gray-200 rounded-md hover:border-blue-300 hover:bg-blue-50 transition-all text-xs">
            <a [href]="parseLinkUrl(link)" (click)="openExternalLink(parseLinkUrl(link), $event)" target="_blank" rel="noopener noreferrer"
                class="text-blue-600 hover:underline truncate max-w-[150px] cursor-pointer">{{ parseLinkName(link) }}</a>
            @if(canManageProject()) {
            <button (click)="removeLink($index)" class="opacity-0 group-hover:opacity-100 p-0.5 text-red-500 hover:bg-red-100 rounded transition-all cursor-pointer">
                <svg class="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            }
        </div>
        }
    </div>
    }

    <!-- Team expanded -->
    @if (showTeamList) {
    <div class="mb-4">
        <div class="flex flex-wrap gap-1.5 p-3 bg-gray-50 rounded-lg border border-gray-100">
            @for (person of fusedTeam; track person.id) {
            <span class="inline-flex items-center gap-1.5 text-xs rounded-lg px-2.5 py-1 font-medium border"
                [class.bg-green-50]="person.role === 'manager'" [class.text-green-700]="person.role === 'manager'" [class.border-green-200]="person.role === 'manager'"
                [class.bg-blue-50]="person.role === 'member'" [class.text-blue-700]="person.role === 'member'" [class.border-blue-200]="person.role === 'member'"
                [class.bg-white]="person.role === 'assignee'" [class.text-gray-600]="person.role === 'assignee'" [class.border-gray-200]="person.role === 'assignee'">
                <span class="inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold"
                    [class.bg-green-200]="person.role === 'manager'" [class.text-green-800]="person.role === 'manager'"
                    [class.bg-blue-200]="person.role === 'member'" [class.text-blue-800]="person.role === 'member'"
                    [class.bg-gray-200]="person.role === 'assignee'" [class.text-gray-700]="person.role === 'assignee'">
                    {{ (person.firstName || person.lastName || '?').charAt(0).toUpperCase() }}
                </span>
                {{ person.firstName }} {{ person.lastName }}
                @if (person.role === 'manager') { <span class="text-green-500 text-[9px] font-bold">MGR</span> }
            </span>
            }
            @empty { <span class="text-xs text-gray-400 italic">No members assigned</span> }

            @if (user.admin()) {
            <button (click)="showManageMembers = true" class="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-blue-600 bg-blue-50 hover:bg-blue-100 text-xs font-semibold cursor-pointer transition-colors border border-blue-200">
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" /></svg>
                Manage
            </button>
            }
        </div>
    </div>
    }

    <!-- Tabs + Phase filter row -->
    <div class="flex items-center justify-between border-b border-gray-200 mb-5 gap-4 flex-wrap">
        <!-- View tabs -->
        <div class="flex items-center gap-6">
            <button (click)="setViewMode('list')" class="pb-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors cursor-pointer"
                [class.text-blue-600]="viewMode === 'list'" [class.border-blue-600]="viewMode === 'list'"
                [class.text-gray-500]="viewMode !== 'list'" [class.border-transparent]="viewMode !== 'list'"
                [class.hover:text-gray-800]="viewMode !== 'list'">List</button>
            <button (click)="setViewMode('kanban')" class="pb-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors cursor-pointer"
                [class.text-blue-600]="viewMode === 'kanban'" [class.border-blue-600]="viewMode === 'kanban'"
                [class.text-gray-500]="viewMode !== 'kanban'" [class.border-transparent]="viewMode !== 'kanban'"
                [class.hover:text-gray-800]="viewMode !== 'kanban'">Kanban</button>
            <button (click)="setViewMode('gantt')" class="pb-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors cursor-pointer"
                [class.text-blue-600]="viewMode === 'gantt'" [class.border-blue-600]="viewMode === 'gantt'"
                [class.text-gray-500]="viewMode !== 'gantt'" [class.border-transparent]="viewMode !== 'gantt'"
                [class.hover:text-gray-800]="viewMode !== 'gantt'">Gantt</button>
        </div>

        <!-- Task filter + Add phase -->
        <div class="flex items-center gap-3 pb-2.5">
            <span class="text-[10px] font-semibold text-gray-400 uppercase tracking-wide shrink-0">Tasks</span>
            <div class="flex items-center bg-gray-100 rounded-lg p-0.5">
                <button (click)="taskStatusFilter = 'open'; onTaskStatusFilterChange()" class="px-2.5 py-1 text-xs font-medium rounded-md transition-all cursor-pointer"
                    [class.bg-white]="taskStatusFilter === 'open'" [class.shadow-sm]="taskStatusFilter === 'open'"
                    [class.text-gray-800]="taskStatusFilter === 'open'" [class.text-gray-500]="taskStatusFilter !== 'open'">Open</button>
                <button (click)="taskStatusFilter = 'all'; onTaskStatusFilterChange()" class="px-2.5 py-1 text-xs font-medium rounded-md transition-all cursor-pointer"
                    [class.bg-white]="taskStatusFilter === 'all'" [class.shadow-sm]="taskStatusFilter === 'all'"
                    [class.text-gray-800]="taskStatusFilter === 'all'" [class.text-gray-500]="taskStatusFilter !== 'all'">All</button>
                <button (click)="taskStatusFilter = 'done'; onTaskStatusFilterChange()" class="px-2.5 py-1 text-xs font-medium rounded-md transition-all cursor-pointer"
                    [class.bg-white]="taskStatusFilter === 'done'" [class.shadow-sm]="taskStatusFilter === 'done'"
                    [class.text-gray-800]="taskStatusFilter === 'done'" [class.text-gray-500]="taskStatusFilter !== 'done'">Done</button>
            </div>
            @if(canManageProject()) {
            <button (click)="showAddStageModal = true" class="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-semibold shadow-sm transition-all cursor-pointer">
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" /></svg>
                Add Phase
            </button>
            }
        </div>
    </div>

    <!-- Content Area -->
    @if(viewMode === "list"){
    <app-list-stage-project [projectStages]="getFilteredStages()" [allTasks]="getFilteredTasks()"
        [projectManagerUserIds]="projectManagerUserIds" [projectStartDate]="project.startDate"
        [encodedHoursByStage]="encodedHoursByStage"
        (stageDeleted)="onStageDeleted($event)" (addTask)="openAddTaskModal($event)"
        (stageChange)="updateStage($event.stageId, $event.changes)" (taskDetailOpen)="openTaskDetailModal($event)"
        (editStage)="editingStage = $event; showEditStageModal = true">
    </app-list-stage-project>
    }@else if(viewMode === 'gantt'){
    <app-gantt-stage-project [projectStages]="getFilteredStages()" [employees]="employees"
        [encodedHoursByStage]="encodedHoursByStage"
        (addTask)="openAddTaskModal($event)" (taskDetailOpen)="openTaskDetailModal($event)"
        (stageChange)="updateStage($event.stageId, $event.changes)">
    </app-gantt-stage-project>
    }@else if(viewMode === 'kanban') {
    <app-kanban-stage-project [projectStages]="getFilteredStages()" [allTasks]="getFilteredTasks()" [tasksByStage]="tasksByStage"
        [projectManagerUserIds]="projectManagerUserIds" [encodedHoursByStage]="encodedHoursByStage"
        (addTask)="openAddTaskModal($event)" (taskDetailOpen)="openTaskDetailModal($event)"
        (taskMoved)="onTaskMoved($event)" (stageChange)="updateStage($event.stageId, $event.changes)">
    </app-kanban-stage-project>
    }
    } @else {
    <div class="text-center py-20 bg-gray-50 rounded-xl border border-dashed border-gray-300">
        <p class="text-gray-500">Project not found.</p>
    </div>
    }

</div>
@if(showAddStageModal && project) {
<div class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in"
    (click)="showAddStageModal = false">
    <div (click)="$event.stopPropagation()" class="w-full flex justify-center">
        <app-add-stage-project [preselectedProjectId]="project.projectId!" [projectStartDate]="project.startDate"
            [projectBudget]="project.estimatedHours || 0"
            [existingPhaseHours]="getProjectAllocatedHours()"
            (close)="showAddStageModal = false">
        </app-add-stage-project>
    </div>
</div>
}

<!-- Edit Stage Modal -->
@if(showEditStageModal && editingStage && project) {
<div class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in"
    (click)="showEditStageModal = false; editingStage = null">
    <div (click)="$event.stopPropagation()" class="w-full flex justify-center">
        <app-edit-stage-project [stage]="editingStage" [projectStartDate]="project.startDate"
            [projectBudget]="project.estimatedHours || 0"
            [existingPhaseHours]="getProjectAllocatedHours() - (editingStage.estimatedHours || 0)"
            (close)="showEditStageModal = false; editingStage = null">
        </app-edit-stage-project>
    </div>
</div>
}

<!-- Task Detail Modal - Rendered at page level (outside all containers) -->


<!-- Manage Team Members Modal -->
@if (showManageMembers && project) {
<app-manage-project-members [projectId]="project.projectId!" [projectName]="project.projectName"
    (close)="closeManageMembers()">
</app-manage-project-members>
}

@if(showUnlinkConfirm) {
<app-confirm-modal
    message="Remove the linked client from this project?"
    (close)="handleUnlinkConfirm($event)">
</app-confirm-modal>
}

<!-- Edit Project Modal -->
@if(showEditProjectModal && project) {
<div class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in"
    (click)="showEditProjectModal = false">
    <div (click)="$event.stopPropagation()" class="w-full flex justify-center">
        <app-edit-project [project]="project" (close)="showEditProjectModal = false">
        </app-edit-project>
    </div>
</div>
}
